import * as Diff from 'diff';
import {
  ContractDates,
  contractDatesMappingValuesUtil,
  ContractDeltaDiff,
  ContractDeltaDoc,
  contractNumbersMappingValuesUtil,
  contractScheduleOfValuesMappingValues,
  ContractSectionsObject,
  ContractSectionsText,
  ContractWorkflowDoc,
  DateUtils,
  IndividualContractSharedUtils,
  ScheduleOfValues,
  ScheduleOfValuesContent,
  SharedTextPatcherUtil,
} from 'common';
import { SharedUtils } from './SharedUtils';

export class ContractDetailsUtils {
  static sortContractDatesUtil<T extends Record<keyof ContractDates, number>>(contractDateKeys: Array<keyof ContractDates>, datesSortingOrder: T): Array<keyof ContractDates> {
    return [...contractDateKeys].sort((a, b) => {
      const orderA = datesSortingOrder[a];
      const orderB = datesSortingOrder[b];

      return orderA - orderB;
    });
  }
}

export class ShareContractUtils extends SharedUtils {
  static findKeyNameUtil = (key: string, value): string => {
    // check if document type object
    if (typeof value === 'object' && value !== null && value.type === 'document' && value.name && value.created_date) {
      return `name: "${value.name} - ${DateUtils.formatIso(value.created_date)}"`;
    }

    // check if contract numbers
    const numbersKey = Object.keys(contractNumbersMappingValuesUtil).find(name => name === key);
    if (numbersKey) {
      const keyField = contractNumbersMappingValuesUtil[numbersKey];
      return `${keyField}: ${value}`;
    }

    // check if contract dates
    const datesKey = Object.keys(contractDatesMappingValuesUtil).find(name => name === key);
    if (datesKey) {
      const keyField = contractDatesMappingValuesUtil[datesKey];
      const removeApostrophe = value.replace(/"/g, '');
      const valueField = DateUtils.format(new Date(removeApostrophe)) ?? 'No Date';
      return `${keyField}: ${valueField}`;
    }

    // check if standard_retainage
    if (key === 'standard_retainage') {
      return `Standard Retainage: ${value}%`;
    } else if (key === 'contract_number') {
      return `Contract Number: ${value}`;
    }

    // check if schedule of values
    const mockScheduleOfValuesContent: ScheduleOfValuesContent = { cost_code: '', cost_description: '', description: '', amount: 0, _id: '' };
    const scheduleOfValuesKey = Object.keys(mockScheduleOfValuesContent).find(name => name === key);
    if (scheduleOfValuesKey) {
      const keyField = contractScheduleOfValuesMappingValues[scheduleOfValuesKey];
      return `${keyField}: ${key === 'amount' ? (value as any)?.toLocaleString() ?? '0' : value}`;
    }

    return `${key}: ${value}`;
  };

  static sortDeltasByWordCountUtil = (deltas: ContractDeltaDoc[]): ContractDeltaDoc[] => {
    return deltas.sort((a, b) => a.diff[0]?.word_count - b.diff[0]?.word_count);
  };

  static sortWorkflowsByIdUtil = (a: string, b: string): number => {
    if (a === 'start') return -1;
    if (b === 'start') return 1;
    if (a === 'finish') return 1;
    if (b === 'finish') return -1;

    if (a.startsWith('step_') && b.startsWith('step_')) {
      const numA = parseInt(a.substring(5));
      const numB = parseInt(b.substring(5));

      return numA - numB;
    }

    if (a.startsWith('step_')) return -1;
    if (b.startsWith('step_')) return 1;

    return a.localeCompare(b);
  };

  static findFinishingWorflowStep(workflowSteps: ContractWorkflowDoc[]): ContractWorkflowDoc {
    return workflowSteps.find(step => step._id === 'finish');
  }
}

export class ReviewApprovalSidebarUtils {
  static cleanTextUtil = (text: string): string => {
    // Create a temporary DOM element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text.replace(/<[^>]*>/g, '');

    return textarea.value.replace(/\n+/g, ' ').trim();
  };

  static extractScheduleOfValuesItem(jsonField: string): string {
    const results: string[] = [];

    // Extract cost_code
    const costCodeMatch = jsonField.match(/"cost_code":\s*"([^"]*)"/);
    if (costCodeMatch && costCodeMatch[1]) {
      results.push(`Cost Code: ${costCodeMatch[1]}`);
    }

    // Extract cost_description
    const costDescMatch = jsonField.match(/"cost_description":\s*"([^"]*)"/);
    if (costDescMatch && costDescMatch[1]) {
      results.push(`Cost Description: ${costDescMatch[1]}`);
    }

    // Extract description
    const descMatch = jsonField.match(/"description":\s*"([^"]*)"/);
    if (descMatch && descMatch[1]) {
      results.push(`Description: ${descMatch[1]}`);
    }

    // Extract children and calculate total amount
    const childrenMatch = jsonField.match(/"children":\s*\[/);
    if (childrenMatch) {
      // Extract all child amounts and sum them
      const childAmountMatches = jsonField.match(
        /"_id":\s*"child_[^"]+"\s*,\s*"cost_code":\s*"[^"]*"\s*,\s*"cost_description":\s*"[^"]*"\s*,\s*"description":\s*"[^"]*"\s*,\s*"amount":\s*([0-9.]+)/g,
      );
      if (childAmountMatches) {
        let totalAmount = 0;
        childAmountMatches.forEach(match => {
          const amountMatch = match.match(/"amount":\s*([0-9.]+)/);
          if (amountMatch) {
            totalAmount += parseFloat(amountMatch[1]);
          }
        });
        results.push(`Amount: ${totalAmount.toLocaleString()}`);
        results.push(`${childAmountMatches.length} child item(s)`);
      }
    } else {
      // No children, extract amount directly
      const amountMatch = jsonField.match(/"amount":\s*([0-9.]+)/);
      if (amountMatch) {
        const amount = parseFloat(amountMatch[1]);
        results.push(`Amount: ${amount.toLocaleString()}`);
      }
    }

    return results.length > 0 ? results.join(', ') : '';
  }

  static extractKeyValue(jsonField: string): string {
    try {
      // Check if this is a document type object
      const documentRegex = /"type":\s*"document"[\s\S]*?"name":\s*"([^"]+)"[\s\S]*?"created_date":\s*"([^"]+)"/;
      const documentMatch = jsonField.match(documentRegex);

      if (documentMatch) {
        const name = documentMatch[1];
        const createdDate = documentMatch[2];
        return `"${name} - ${DateUtils.formatIso(createdDate)} : ${DateUtils.getHoursWithTimezone(createdDate)}"`;
      }

      // Check if this is a schedule of values item (has _id pattern and cost_code/cost_description)
      const scheduleItemRegex = /"_id":\s*"(parent_|child_)[^"]+"/;
      const scheduleItemMatch = jsonField.match(scheduleItemRegex);

      if (scheduleItemMatch) {
        const result = ReviewApprovalSidebarUtils.extractScheduleOfValuesItem(jsonField);
        if (result) return result;
      }

      // Default behavior for other fields
      const regex = /"([^"]+)":\s*(.+)/;
      const match = jsonField.match(regex);

      if (!match || match.length < 3) {
        return null;
      }

      const key = match[1];

      let value;
      try {
        const tempJson = `{${jsonField}}`;
        const parsed = JSON.parse(tempJson);
        value = parsed[key];
      } catch {
        value = match[2].trim();

        if (value.endsWith(',')) {
          value = value.slice(0, -1);
        }
      }

      return ShareContractUtils.findKeyNameUtil(key, value);
    } catch (error) {
      console.error('Failed to extract key-value pair:', error);
      return '';
    }
  }
}

export class InidividualContractPageUtils {
  static adjustAllDiffBasedOnPreviousAppliedDeltasUtil = (
    diffs: ContractDeltaDiff[],
    previousAppliedDeltas: ContractDeltaDoc[],
    section: ContractSectionsText | ContractSectionsObject,
  ): ContractDeltaDiff[] => {
    const filteredPreviousAppliedDeltas = previousAppliedDeltas.filter(doc => doc.section_id === section);
    return diffs.map(diff => {
      const newWordCount = InidividualContractPageUtils.adjustSingleDiffBasedOnPreviousAppliedDeltasUtil(diff, filteredPreviousAppliedDeltas);
      return { ...diff, word_count: newWordCount };
    });
  };
  static adjustSingleDiffBasedOnPreviousAppliedDeltasUtil = (diff: ContractDeltaDiff, previousAppliedDeltas: ContractDeltaDoc[], remove: boolean = true): number => {
    let valueToSubtract: number = diff.word_count;
    for (const doc of previousAppliedDeltas) {
      for (const patch of doc.diff) {
        if (patch.word_count < diff.word_count) {
          if (patch.added) {
            valueToSubtract = remove ? valueToSubtract - patch.count : valueToSubtract + patch.count;
          }
          if (patch.removed) {
            valueToSubtract = remove ? valueToSubtract + patch.count : valueToSubtract - patch.count;
          }
        }
      }
    }
    return valueToSubtract;
  };
  static adjustSingleDiffForCollisions = (diff: ContractDeltaDiff, previousAppliedDeltas: ContractDeltaDoc[], remove: boolean = true): number => {
    let valueToSubtract: number = diff.word_count;
    const sortedDeltas = ShareContractUtils.sortDeltasByWordCountUtil(previousAppliedDeltas);
    for (const doc of sortedDeltas) {
      for (const patch of doc.diff) {
        if (patch.word_count + patch.count < valueToSubtract) {
          if (patch.added) {
            valueToSubtract = remove ? valueToSubtract - patch.count : valueToSubtract + patch.count;
          }
          if (patch.removed) {
            valueToSubtract = remove ? valueToSubtract + patch.count : valueToSubtract - patch.count;
          }
        }
      }
    }
    return valueToSubtract;
  };

  static filteredCollidingDeltas(
    diff: ContractDeltaDiff[],
    deltas: ContractDeltaDoc[],
    changedSection: ContractSectionsText | ContractSectionsObject,
  ): { colliding: ContractDeltaDoc[]; nonColliding: ContractDeltaDoc[] } {
    const filteredDeltas = deltas.filter(doc => doc.section_id === changedSection);
    const firstArray = diff;
    const second = filteredDeltas.map(delta => ({ id: delta._id, diff: delta.diff }));
    let collidingIds = [];
    for (let i = 0; i < firstArray.length; i++) {
      const firstElement = firstArray[i];
      const insertionPoint = firstElement.word_count;
      const insertionPointEnd = insertionPoint + firstElement.count;

      for (let j = 0; j < second.length; j++) {
        const secondSubarray = second[j].diff;
        let hasCollision = false;

        for (let k = 0; k < secondSubarray.length; k++) {
          const secondElement = secondSubarray[k];
          const secondStart = secondElement.word_count;
          const secondEnd = secondElement.word_count + secondElement.count;

          if (insertionPoint <= secondEnd && secondStart <= insertionPointEnd) {
            collidingIds.push(second[j].id);
            hasCollision = true;
            break;
          }
        }

        if (hasCollision) {
          break;
        }
      }
    }

    collidingIds = [...new Set(collidingIds)];
    const nonColliding = [];
    const colliding = [];
    deltas.forEach(delta => {
      if (collidingIds.includes(delta._id)) {
        colliding.push(delta);
      } else {
        nonColliding.push(delta);
      }
    });

    return { colliding, nonColliding };
  }

  static currentVersionDeltasBeforeCollision(diff: ContractDeltaDiff[], currentDeltas: ContractDeltaDoc[], section: ContractSectionsText): ContractDeltaDiff[] {
    const filteredPreviousAppliedDeltas = currentDeltas.filter(doc => doc.section_id === section);
    return diff.map(diff => {
      const newWordCount = InidividualContractPageUtils.adjustSingleDiffForCollisions(diff, filteredPreviousAppliedDeltas, true);
      return { ...diff, word_count: newWordCount };
    });
  }

  static removeHtmlPatchesFromSection(section: ContractSectionsText | ContractSectionsObject, sectionInitialText: string | object, documents: ContractDeltaDoc[]): string {
    const contractPatcher = new TextPatcherUtil();
    const sectionPatches = documents.filter(doc => doc.section_id === section).map(doc => doc.diff);
    const patchesMap = IndividualContractSharedUtils.createPatchesMapUtil(sectionPatches);

    return typeof sectionInitialText === 'string'
      ? contractPatcher.removeHtmlPatches(sectionInitialText, patchesMap)
      : contractPatcher.removeJsonPatches(sectionInitialText, patchesMap);
  }
}

export class TextPatcherUtil extends SharedTextPatcherUtil {
  static formatHTMLForDiff(html: string): string {
    // Normalize whitespace within HTML tags - this is the key addition
    html = html.replace(/>(\s+)</g, '><');
    // Normalize whitespace between HTML tags - this is the key fix
    html = html.replace(/>\s+</g, '><');
    html = html.replace(/<\/(p|div|h[1-6]|li|ol|span)>(?!\n)/gi, '</$1>\n');
    html = html.replace(/<ol([^>]*)>/gi, '<ol$1>\n');
    return html
      .split('\n')
      .filter(function (line) {
        return line.length > 0;
      })
      .join('\n');
  }

  static removeHtmlDiffFormatting(text: string): string {
    let html = text.split('\n').join('\n');
    html = html.replace(/(<\/(p|div|h[1-6]|li|ol|span)>)\n/gi, '$1');
    return html;
  }

  removeHtmlPatches(originalText: string, patches: Map<number, ContractDeltaDiff[]>): string {
    let words = this.segmentText(originalText);
    const sortedPatches = Array.from(patches.entries()).sort(([a], [b]) => a - b);

    for (const [index, patchList] of sortedPatches) {
      const newWords = [...words];

      const additions = patchList.filter(p => p.added);
      for (const addition of additions) {
        const addWords = this.segmentText(addition.value);
        newWords.splice(index, addWords.length);
      }

      const removals = patchList.filter(p => p.removed);
      for (const removal of removals) {
        const restoreWords = this.segmentText(removal.value);
        newWords.splice(index, 0, ...restoreWords);
      }

      words = [...newWords];
    }
    return words.join('').replace(/\s+/g, ' ');
  }

  computeTextDiff = (oldText: string, newText: string): ContractDeltaDiff[] => {
    const formatedOldText = TextPatcherUtil.formatHTMLForDiff(oldText);
    const formatedNewText = TextPatcherUtil.formatHTMLForDiff(newText);
    let diff = Diff.diffWords(formatedOldText, formatedNewText, { intlSegmenter: this.tokenizer, ignoreWhitespace: true });
    let wordCount = 0;
    const edits: ContractDeltaDiff[] = [];

    if (diff.length > 4) {
      diff = Diff.diffLines(formatedOldText, formatedNewText);
      diff = diff.map(changedDiff => ({ ...changedDiff, count: this.segmentText(changedDiff.value).length }));
    }

    diff.forEach(patch => {
      if (patch.added || patch.removed) {
        const valueToAppend: ContractDeltaDiff = {
          word_count: wordCount,
          count: patch.count,
          added: patch.added,
          removed: patch.removed,
          value: patch.value,
        };
        edits.push(valueToAppend);
      }
      if (!patch.removed && patch.count) {
        wordCount += patch.count;
      }
    });

    return edits;
  };

  static computeJsonDiff = (oldObject: string | object, newObject: string | object): ContractDeltaDiff[] => {
    const jsonSegment = new TextPatcherUtil();
    const oldString = jsonSegment.segmentJson(oldObject).join('');
    const newString = jsonSegment.segmentJson(newObject).join('');
    let diff = Diff.diffLines(oldString, newString, { newlineIsToken: true });
    let wordCount = 0;
    const edits: ContractDeltaDiff[] = [];

    diff.forEach(patch => {
      if (patch.added || patch.removed) {
        const valueToAppend: ContractDeltaDiff = {
          word_count: wordCount,
          count: patch.count,
          added: patch.added,
          removed: patch.removed,
          value: patch.value,
        };
        edits.push(valueToAppend);
      }
      if (!patch.removed && patch.count) {
        wordCount += patch.count;
      }
    });

    return edits;
  };

  removeJsonPatches(originalText: object, patches: Map<number, ContractDeltaDiff[]>): string {
    let words = this.segmentJson(originalText);
    const sortedPatches = Array.from(patches.entries()).sort(([a], [b]) => a - b);

    for (const [index, patchList] of sortedPatches) {
      const additions = patchList.filter(p => p.added);
      for (const addition of additions) {
        const addWords = addition.count;
        words.splice(index, addWords);
      }

      const removals = patchList.filter(p => p.removed);
      for (const removal of removals) {
        const restoreWords = removal.value;
        words.splice(index, 0, restoreWords);
      }
    }
    return words.join('');
  }

  areTags(text: string) {
    let openCount = 0;
    let closeCount = 0;

    for (let i = 0; i < text.length; i++) {
      if (text[i] === '<') {
        openCount++;
      }

      if (text[i] === '/' && i + 1 < text.length && text[i + 1] === '>') {
        closeCount++;
      }
    }
    return openCount === closeCount;
  }

  static loopBackwardsUntilAreTags = (text: string) => {
    let counter = 1;
    for (let i = 1; i < text.length - 1; i++) {
      const valueToTest = text.slice(0, text.length - counter);

      const isValid = TextPatcherUtil.hasMatchingTags(valueToTest);
      if (isValid) {
        const textToReturn = text.slice(-i);
        return textToReturn;
      }
      counter++;
    }
    return text;
  };

  static hasMatchingTags(input: string) {
    const content = input.replace(/^'|'$/g, '');
    const processed = content.replace(/<[^>]*>/g, '');
    return !processed.includes('<') && !processed.includes('>');
  }
}

export class ScheduleOfValuesUtils {
  static calculateScheduleOfValuesTotalAmountUtil = (data?: ScheduleOfValues[]): number => {
    if (!data || data.length === 0) return 0;

    let totalSum = 0;
    for (const node of data) {
      totalSum += ScheduleOfValuesUtils.getNodeAmountUtil(node);
    }

    return totalSum;
  };
  static getNodeAmountUtil = (node: ScheduleOfValues): number => {
    if (node.children && node.children.length > 0) {
      return node.children.reduce((sum, child) => sum + ScheduleOfValuesUtils.getNodeAmountUtil(child), 0);
    }
    return node.amount;
  };
}
